import React from 'react';
import './public/site.css';
import PublicHeader from './public/PublicHeader';
import PublicFooter from './public/PublicFooter';

// Icon components
const BrushIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Brush">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M4 20h10l2-6H6z" fill={color} opacity=".15" />
			<path d="M14 9l5-5" />
			<path d="M13 8l3 3" />
			<path d="M6 14h10" />
		</g>
	</svg>
);

const ConstructIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Construct">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" fill={color} opacity=".15" />
			<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
		</g>
	</svg>
);

const HomeIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Home">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill={color} opacity=".15" />
			<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
			<polyline points="9,22 9,12 15,12 15,22" />
		</g>
	</svg>
);

const HeartIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = 'currentColor' }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Heart">
		<g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill={color} opacity=".15" />
			<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
		</g>
	</svg>
);

const LaoDongPhoThongEN: React.FC = () => {
	React.useEffect(() => {
		document.title = 'General labor - Fconnect';
	}, []);

	return (
		<div className="home-page fc-site">
			<PublicHeader lang="en" />

			{/* Banner Section */}
			<section className="ldt-banner-section">
				<div className="ldt-banner-content">
					<div className="ldt-banner-text">
						<h2>High-quality general labor services</h2>
						<p>Comprehensive solutions for every home and office need</p>
					</div>
					<div className="ldt-banner-visual">
						<div className="ldt-banner-icons">
							<BrushIcon size={48} />
							<ConstructIcon size={48} />
							<HomeIcon size={48} />
							<HeartIcon size={48} />
						</div>
					</div>
				</div>
			</section>

			{/* Content */}
			<section className="home-container">
				<header className="ldt-hero">
					<h1>General labor</h1>
					<p className="ldt-subtitle">Household and everyday manpower solutions: fast, reliable, transparent pricing.</p>
				</header>

				<div className="ldt-content">
					<section className="ldt-section">
						<h2 className="section-title">Services</h2>
						<div className="services-grid">
							<div className="service-card">
								<div className="service-icon"><BrushIcon size={56} /></div>
								<h4>House cleaning</h4>
								<p>Basic cleaning, laundry, tidying and organizing your space.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><ConstructIcon size={56} /></div>
								<h4>Electrical and plumbing repairs</h4>
								<p>Inspect and fix minor issues of electricity, water, and sanitary ware.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><HomeIcon size={56} /></div>
								<h4>Home repairs</h4>
								<p>Painting, patching, basic installations, and small maintenance tasks.</p>
							</div>
							<div className="service-card">
								<div className="service-icon"><HeartIcon size={56} /></div>
								<h4>Personal care</h4>
								<p>Support for the elderly, light patient care, and babysitting by the hour.</p>
							</div>
						</div>
					</section>

					<section className="ldt-section">
						<h2 className="section-title">Hourly packages</h2>
						<div className="packages-grid">
							<div className="package-card">
								<div className="package-header">
									<h3>2-hour package</h3>
									<p className="package-sub">Best for light tasks</p>
								</div>
								<ul className="package-list">
									<li>Quick cleaning of main areas</li>
									<li>Minor checks/repairs</li>
									<li>Flexible time</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>3-hour package</h3>
									<p className="package-sub">Handle a complete task</p>
								</div>
								<ul className="package-list">
									<li>More detailed cleaning</li>
									<li>Light home repairs</li>
									<li>Cost-effective</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>4-hour package</h3>
									<p className="package-sub">Multiple tasks in one visit</p>
								</div>
								<ul className="package-list">
									<li>Cleaning, laundry, organizing</li>
									<li>General electrical and plumbing check</li>
									<li>Flexible to your needs</li>
								</ul>
							</div>

							<div className="package-card">
								<div className="package-header">
									<h3>Full-day package</h3>
									<p className="package-sub">All-day service for bigger needs</p>
								</div>
								<ul className="package-list">
									<li>Comprehensive cleaning</li>
									<li>Minor repairs on request</li>
									<li>Daytime personal care</li>
								</ul>
							</div>
						</div>
					</section>
				</div>
			</section>

			<PublicFooter lang="en" />
		</div>
	);
};

export default LaoDongPhoThongEN;


